import type {
  AppState,
  ArkItemOptionDto,
  BeaconImportSummary,
  BeaconSetSummary,
  ConfigFileName,
  ConfigOverride,
  ConfigSetCreatureAdjustmentDto,
  ClusterPowerAction,
  ClusterPowerResult,
  CraftingIngredientDto,
  CraftingRecipeDto,
  CreatureAdjustmentDto,
  DeploymentRecord,
  DeploymentCredentials,
  DeploymentSchedule,
  GeneralSettingChangeDto,
  GeneralSettingDto,
  HotZoneConfigDto,
  IniEntryDto,
  LootDropDto,
  SpawnContainerDto
} from "../shared/types";

export interface Bootstrap {
  project: {
    name: string;
    gameId: string;
    projectId?: string;
    savedWith?: number;
    difficulty?: number;
    isConsole?: boolean;
    encryptedServerDataPresent: boolean;
  };
  state: AppState;
  editors: Array<{ id: string; label: string; description: string; icon: string }>;
  files: Array<{
    file: ConfigFileName;
    bytes: number;
    entries: number;
    sections: number;
    categories: Record<string, number>;
  }>;
  validation: Array<{ file: ConfigFileName; level: "warning" | "error"; message: string }>;
  beacon?: BeaconImportSummary;
  integrations: {
    nitrado: {
      configured: boolean;
      authMode: string;
      tokenExposed: boolean;
      source: "saved" | "environment" | "none";
    };
  };
  catalogs: {
    creatures: number;
    lootDrops: number;
    generalSettings: number;
  };
}

export interface ConfigSetDetail {
  set: BeaconSetSummary;
  activeProfileIds: string[];
  creatures: Array<{ className: string; name: string; contentPackName?: string }>;
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      ...(options?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...options?.headers
    }
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(body.error ?? `${response.status} ${response.statusText}`, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

export const api = {
  authStatus: () => request<{ required: boolean; authenticated: boolean }>("/api/auth/status"),
  login: (password: string) =>
    request<{ ok: true }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password })
    }),
  logout: () => request<void>("/api/auth/logout", { method: "POST", body: "{}" }),
  bootstrap: () => request<Bootstrap>("/api/bootstrap"),

  generalSettings: (profileId: string) =>
    request<{ items: GeneralSettingDto[]; total: number; groups: string[] }>(
      `/api/general/${encodeURIComponent(profileId)}`
    ),

  saveGeneralSettings: (profileId: string, changes: GeneralSettingChangeDto[]) =>
    request<{ ok: true; items: GeneralSettingDto[] }>(
      `/api/general/${encodeURIComponent(profileId)}`,
      { method: "PUT", body: JSON.stringify({ changes }) }
    ),

  craftingRecipes: (profileId: string, options: { q: string; page: number; limit?: number }) => {
    const params = new URLSearchParams({
      q: options.q,
      page: String(options.page),
      limit: String(options.limit ?? 30)
    });
    return request<{
      items: CraftingRecipeDto[];
      resources: Array<{ className: string; name: string }>;
      total: number;
      page: number;
      pages: number;
    }>(`/api/crafting/${encodeURIComponent(profileId)}/recipes?${params}`);
  },

  saveCraftingRecipe: (
    profileId: string,
    entryId: string,
    ingredients: CraftingIngredientDto[]
  ) =>
    request<{ ok: true; recipe: CraftingRecipeDto }>(
      `/api/crafting/${encodeURIComponent(profileId)}/recipes/${encodeURIComponent(entryId)}`,
      { method: "PUT", body: JSON.stringify({ ingredients }) }
    ),

  creatures: (profileId: string, options: { q: string; page: number; limit?: number }) => {
    const params = new URLSearchParams({
      q: options.q,
      page: String(options.page),
      limit: String(options.limit ?? 30)
    });
    return request<{ items: CreatureAdjustmentDto[]; total: number; page: number; pages: number }>(
      `/api/creatures/${encodeURIComponent(profileId)}?${params}`
    );
  },

  saveCreature: (
    profileId: string,
    className: string,
    values: Pick<
      CreatureAdjustmentDto,
      | "wildDamage"
      | "wildResistance"
      | "tamedDamage"
      | "tamedResistance"
      | "prohibitSpawning"
      | "preventTaming"
      | "prohibitTransfer"
      | "prohibitBreeding"
    >
  ) =>
    request<{ ok: true; creature: CreatureAdjustmentDto }>(
      `/api/creatures/${encodeURIComponent(profileId)}/${encodeURIComponent(className)}`,
      { method: "PUT", body: JSON.stringify(values) }
    ),

  spawnCatalog: () =>
    request<{
      creatures: ArkItemOptionDto[];
      spawnPoints: ArkItemOptionDto[];
    }>("/api/spawns/catalog"),

  spawnContainers: (
    profileId: string,
    options: { q: string; page: number; limit?: number }
  ) => {
    const params = new URLSearchParams({
      q: options.q,
      page: String(options.page),
      limit: String(options.limit ?? 25)
    });
    return request<{
      items: SpawnContainerDto[];
      total: number;
      page: number;
      pages: number;
    }>(`/api/spawns/${encodeURIComponent(profileId)}?${params}`);
  },

  spawnContainer: (profileId: string, entryId: string) =>
    request<SpawnContainerDto>(
      `/api/spawns/${encodeURIComponent(profileId)}/${encodeURIComponent(entryId)}`
    ),

  createSpawnContainer: (profileId: string, container: SpawnContainerDto) =>
    request<{ ok: true; container: SpawnContainerDto }>(
      `/api/spawns/${encodeURIComponent(profileId)}`,
      { method: "POST", body: JSON.stringify(container) }
    ),

  saveSpawnContainer: (
    profileId: string,
    entryId: string,
    container: SpawnContainerDto
  ) =>
    request<{ ok: true; container: SpawnContainerDto }>(
      `/api/spawns/${encodeURIComponent(profileId)}/${encodeURIComponent(entryId)}`,
      { method: "PUT", body: JSON.stringify(container) }
    ),

  lootDrops: (profileId: string, options: { q: string; page: number; limit?: number }) => {
    const params = new URLSearchParams({
      q: options.q,
      page: String(options.page),
      limit: String(options.limit ?? 30)
    });
    return request<{ items: LootDropDto[]; total: number; page: number; pages: number }>(
      `/api/loot/${encodeURIComponent(profileId)}/drops?${params}`
    );
  },

  lootDrop: (profileId: string, entryId: string) =>
    request<LootDropDto>(
      `/api/loot/${encodeURIComponent(profileId)}/drops/${encodeURIComponent(entryId)}`
    ),

  lootItems: () => request<ArkItemOptionDto[]>("/api/loot/items/catalog"),

  saveLootDrop: (profileId: string, entryId: string, drop: LootDropDto) =>
    request<{ ok: true; drop: LootDropDto }>(
      `/api/loot/${encodeURIComponent(profileId)}/drops/${encodeURIComponent(entryId)}`,
      { method: "PUT", body: JSON.stringify(drop) }
    ),

  entries: (
    profileId: string,
    file: ConfigFileName,
    options: { category: string; q: string; page: number; limit?: number }
  ) => {
    const params = new URLSearchParams({
      category: options.category,
      q: options.q,
      page: String(options.page),
      limit: String(options.limit ?? 40)
    });
    return request<{ items: IniEntryDto[]; total: number; page: number; pages: number }>(
      `/api/config/${encodeURIComponent(profileId)}/${encodeURIComponent(file)}/entries?${params}`
    );
  },

  entry: (profileId: string, file: ConfigFileName, entryId: string) =>
    request<IniEntryDto>(
      `/api/config/${encodeURIComponent(profileId)}/${encodeURIComponent(file)}/entry/${encodeURIComponent(entryId)}`
    ),

  saveEntry: (profileId: string, override: Partial<ConfigOverride>) =>
    request<{ ok: true; override: ConfigOverride }>(`/api/config/${encodeURIComponent(profileId)}/entry`, {
      method: "PUT",
      body: JSON.stringify(override)
    }),

  removeOverride: (profileId: string, overrideId: string) =>
    request<void>(
      `/api/config/${encodeURIComponent(profileId)}/override/${encodeURIComponent(overrideId)}`,
      { method: "DELETE" }
    ),

  importConfig: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<{ ok: true }>("/api/config/import", { method: "POST", body: form });
  },

  patchProfile: (profileId: string, patch: Record<string, unknown>) =>
    request(`/api/profiles/${encodeURIComponent(profileId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),

  configSet: (configSetId: string) =>
    request<ConfigSetDetail>(`/api/config-sets/${encodeURIComponent(configSetId)}`),

  saveConfigSet: (
    configSetId: string,
    creatureAdjustments: ConfigSetCreatureAdjustmentDto[],
    activeProfileIds: string[],
    hotZone?: HotZoneConfigDto
  ) =>
    request<{ ok: true; set: BeaconSetSummary; activeProfileIds: string[] }>(
      `/api/config-sets/${encodeURIComponent(configSetId)}`,
      {
        method: "PUT",
        body: JSON.stringify({ creatureAdjustments, activeProfileIds, hotZone })
      }
    ),

  patchServer: (serverId: string, patch: Record<string, unknown>) =>
    request(`/api/servers/${encodeURIComponent(serverId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),

  refreshNitrado: () => request("/api/nitrado/refresh", { method: "POST", body: "{}" }),

  clusterPower: (action: ClusterPowerAction) =>
    request<{
      action: ClusterPowerAction;
      total: number;
      accepted: number;
      skipped: number;
      failed: number;
      results: ClusterPowerResult[];
    }>("/api/servers/power", {
      method: "POST",
      body: JSON.stringify({ action, confirm: action === "start" ? "START ALL" : "STOP ALL" })
    }),

  saveNitradoToken: (token: string) =>
    request<{ ok: true; configured: true; source: "saved"; serviceCount: number }>("/api/nitrado/token", {
      method: "POST",
      body: JSON.stringify({ token })
    }),

  testNitrado: () =>
    request<{ ok: true; serviceCount: number }>("/api/nitrado/test", {
      method: "POST",
      body: "{}"
    }),

  removeNitradoToken: () =>
    request<{ ok: true; configured: boolean; source: "environment" | "none" }>("/api/nitrado/token", {
      method: "DELETE"
    }),

  previewDeploy: (
    profileId: string,
    serverIds: string[],
    credentials: DeploymentCredentials,
    startServersAfterDeploy: boolean,
    activeConfigSets: string[]
  ) =>
    request<{
      profile: { id: string; name: string; activeConfigSets: string[] };
      servers: Array<{ id: string; name: string; map: string }>;
      files: Array<{ file: string; bytes: number; sha256: string; overrides: number; perServer?: boolean }>;
      warnings: string[];
    }>("/api/deploy/preview", {
      method: "POST",
      body: JSON.stringify({ profileId, serverIds, credentials, startServersAfterDeploy, activeConfigSets })
    }),

  deploy: (
    profileId: string,
    serverIds: string[],
    dryRun: boolean,
    credentials: DeploymentCredentials,
    startServersAfterDeploy: boolean,
    activeConfigSets: string[]
  ) =>
    request<DeploymentRecord>("/api/deploy", {
      method: "POST",
      body: JSON.stringify({
        profileId,
        serverIds,
        dryRun,
        credentials,
        startServersAfterDeploy,
        activeConfigSets,
        confirm: dryRun ? "" : "DEPLOY"
      })
    }),

  deployment: (deploymentId: string) =>
    request<DeploymentRecord>(`/api/deployments/${encodeURIComponent(deploymentId)}`),

  createSchedule: (schedule: Partial<DeploymentSchedule>) =>
    request<DeploymentSchedule>("/api/schedules", {
      method: "POST",
      body: JSON.stringify(schedule)
    }),

  patchSchedule: (scheduleId: string, patch: Partial<DeploymentSchedule>) =>
    request<DeploymentSchedule>(`/api/schedules/${encodeURIComponent(scheduleId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),

  deleteSchedule: (scheduleId: string) =>
    request<void>(`/api/schedules/${encodeURIComponent(scheduleId)}`, { method: "DELETE" })
};
